import { useRecoilValue } from "recoil";
import { useNavigate } from "react-router-dom";
import useIsInappWebview from "./useIsInappWebview";
import { sendMessage } from "utils/webview";
import { userAtom } from "recoil/userAtom";
import { patientAtom } from "recoil/patientAtom";

export default function useOpenTask({
  taskKey,
  thoughtRecordKey,
  meditationRecordKey,
  params,
}: {
  taskKey: string;
  thoughtRecordKey?: string;
  meditationRecordKey?: string;
  params?: { [key: string]: string };
}) {
  const navigate = useNavigate();
  const isInappWebview = useIsInappWebview();
  const user = useRecoilValue(userAtom);
  const patientState = useRecoilValue(patientAtom);
  const pid = patientState?.patientId;

  const encodedTaskKey = encodeURIComponent(taskKey);
  const newPathName = `/${encodedTaskKey}`;

  function goTask() {
    // Build query for in-app navigation (browser) - include trk/mrk and any extra params
    const browserQuery = new URLSearchParams();
    if (thoughtRecordKey) {
      browserQuery.set("trk", thoughtRecordKey);
    } else if (meditationRecordKey) {
      browserQuery.set("mrk", meditationRecordKey);
    }
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value != null) browserQuery.set(key, value);
      });
    }

    if (window.location.pathname === newPathName || !isInappWebview) {
      navigate(
        `${newPathName}${browserQuery.toString() ? `?${browserQuery.toString()}` : ``}`,
        { replace: window.location.pathname === newPathName }
      );
    } else {
      if (isInappWebview) {
        if (taskKey === "payment") {
          // Append params if provided for payment modal too
          const paymentQuery = new URLSearchParams();
          if (params) {
            Object.entries(params).forEach(([key, value]) => {
              if (value != null) paymentQuery.set(key, value);
            });
          }
          sendMessage(
            JSON.stringify({
              event: "open_modal",
              params: {
                url: `${window.location.origin}/payment${paymentQuery.toString() ? `?${paymentQuery.toString()}` : ""}`,
                type: "transparentModal",
              },
            })
          );
        } else {
          const sp = new URLSearchParams();
          // 코치(JWT 세션)는 userAtom 에 토큰이 없다 — 새 탭은 리프레시 쿠키로 세션을 복원한다.
          if (user?.accessToken) sp.set("at", user.accessToken);
          if (pid) sp.set("pid", String(pid));
          if (thoughtRecordKey) {
            sp.set("trk", thoughtRecordKey);
          } else if (meditationRecordKey) {
            sp.set("mrk", meditationRecordKey);
          } else {
            sp.set("ts", Date.now().toString());
          }
          if (params) {
            Object.entries(params).forEach(([key, value]) => {
              if (value != null) sp.set(key, value);
            });
          }
          const url = `${window.location.origin}${newPathName}?${sp.toString()}`;

          sendMessage(
            JSON.stringify({
              event: "navigate",
              params: {
                url: url,
              },
            })
          );
        }
      }
    }
  }

  return goTask;
}
